export type Token =
  | {
      type: "text";
      id: string;
      node: Text;
      content: string;
      meta?: Record<string, any>;
    }
  | {
      type: "space";
      id: string;
      node: Text;
      content: string;
      meta?: Record<string, any>;
    }
  | {
      type: "element";
      id: string;
      node: HTMLElement;
      tagName: string;
      meta?: Record<string, any>;
    }
  | {
      type: "br";
      id: string;
      node: HTMLElement;
      tagName: "br";
      meta?: Record<string, any>;
    }
  | {
      type: "other";
      id: string;
      node: Node;
      meta?: Record<string, any>;
    };

let tokenCounter = 0;

/**
 * Builds an array of tokens from a given list of child nodes.
 *
 * This function processes each node in the provided `NodeListOf<ChildNode>` and generates
 * a corresponding token object based on the node type. The tokens can represent elements,
 * text nodes, whitespace, or other types of nodes.
 *
 * @param nodes - A collection of child nodes to be processed into tokens.
 * @returns An array of tokens, where each token represents a node with additional metadata.
 *
 * Token Types:
 * - `element`: Represents an HTML element node, including its tag name.
 * - `text`: Represents a text node with non-whitespace content.
 * - `space`: Represents a text node with only whitespace content.
 * - `other`: Represents any other type of node.
 *
 * Each token includes:
 * - `type`: The type of the token (`element`, `text`, `space`, or `other`).
 * - `id`: A unique identifier for the token.
 * - `node`: The original DOM node.
 * - Additional properties depending on the token type:
 *   - `tagName` for `element` tokens.
 *   - `content` for `text` and `space` tokens.
 */
export function BuildTokens(
  nodes: NodeListOf<ChildNode>
): Token[] {
  tokenCounter = 0;
  const tokens: Token[] = [];

  nodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;

      if (el.tagName.toLowerCase() === "br") {
        tokens.push({
          type: "br",
          id: `br_${tokenCounter++}`,
          node: el,
          tagName: "br",
        });
        return;
      }

      tokens.push({
        type: "element",
        id: `el_${tokenCounter++}`,
        node: el,
        tagName: el.tagName.toLowerCase(),
      });
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue ?? "";
      const id = `text_${tokenCounter++}`;

      if (text.trim()) {
        tokens.push({
          type: "text",
          id,
          node: node as Text,
          content: text,
        });
      } else {
        tokens.push({
          type: "space",
          id,
          node: node as Text,
          content: text,
        });
      }
    } else {
      tokens.push({
        type: "other",
        id: `node_${tokenCounter++}`,
        node,
      });
    }
  });

  return tokens;
}
